import {
    Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton,
    LinearProgress, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { useVisualizadorArquivos } from "context/VisualizadorArquivosContext";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PERMISSAO_FILA_SOLICITACOES } from "utils/Perfis";
import { get, getFile, post, postNoResponse } from "utils/WebService";
import BaseDeDadosCreateEdit from "../baseDeDados/BaseDeDadosCreateEdit";
import {
    TIPOS_SOLICITACAO, acaoHistorico, alvoSolicitacao, comAlfa, corIdade, correspondeBusca, formatarDuracao, listaDe,
    tipoSolicitacao,
} from "./solicitacaoUtils";
import { ESCURO } from "assets/coresTema";

// As três listas da tela; o indicador de cada uma troca a lista exibida
const VISOES = {
    fila: { rotulo: "Na fila", icone: "inbox", cor: "#00695C", corEscura: "#4DB6AC" },
    aguardando: { rotulo: "Aguardando solicitante", icone: "hourglass_top", cor: "#B26A00", corEscura: "#FFB74D" },
    concluidas: { rotulo: "Concluídas", icone: "task_alt", cor: "#2E7D32", corEscura: "#81C784" },
};
const COR_TEMPO_MEDIO = { light: "#455A64", dark: "#B0BEC5" };

const OPCAO_TODOS = { id: "", value: "", label: "Todos" };
const TIPOS_OPTIONS = [OPCAO_TODOS, ...Object.entries(TIPOS_SOLICITACAO).map(([value, t]) => ({ id: value, value, label: t.label }))];
const PERIODOS_OPTIONS = [7, 30, 90].map(d => ({ id: d, value: d, label: `Últimos ${d} dias` }));
const ATUALIZACAO_OPTIONS = [
    { id: 0, value: 0, label: "Desativada" },
    { id: 15, value: 15, label: "15s" },
    { id: 30, value: 30, label: "30s" },
    { id: 60, value: 60, label: "1 min" },
    { id: 120, value: 120, label: "2 min" },
];
const LIMITE_INICIAL = 20;
const rotuloFiltro = { px: 1, pb: 0.5, display: "block" };
const celula = { verticalAlign: "top", pt: 1.5 };

function Indicador({ icone, cor, rotulo, valor, detalhe, selecionado, onClick }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const clicavel = typeof onClick === "function";
    return (
        <Card
            variant="outlined"
            onClick={onClick}
            role={clicavel ? "button" : undefined}
            tabIndex={clicavel ? 0 : undefined}
            aria-pressed={clicavel ? !!selecionado : undefined}
            onKeyDown={(e) => { if (clicavel && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(); } }}
            sx={{
                p: 2,
                height: "100%",
                cursor: clicavel ? "pointer" : "default",
                background: darkMode ? ESCURO.cartao : "#fafafa",
                borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                boxShadow: selecionado ? `inset 0 0 0 2px ${cor}, 0 4px 14px ${comAlfa(cor, 0.25)}` : "none",
                transition: "box-shadow 0.15s ease-in-out",
                "&:hover": clicavel && !selecionado ? { boxShadow: `inset 0 0 0 1px ${cor}` } : undefined,
            }}
        >
            <MDBox display="flex" alignItems="flex-start" gap={2}>
                <MDBox display="flex" alignItems="center" justifyContent="center"
                    sx={{ width: 48, height: 48, minWidth: 48, borderRadius: "50%", background: comAlfa(cor, darkMode ? 0.22 : 0.12) }}>
                    <Icon sx={{ color: cor, fontSize: "1.5rem !important" }}>{icone}</Icon>
                </MDBox>
                <MDBox minWidth={0}>
                    <MDTypography variant="button" color="text" sx={{ display: "block", fontWeight: 500 }}>{rotulo}</MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ lineHeight: 1.25 }}>{valor}</MDTypography>
                    {detalhe && (
                        <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.85 }}>{detalhe}</MDTypography>
                    )}
                </MDBox>
            </MDBox>
        </Card>
    );
}

function TipoSolicitacao({ codigo, darkMode }) {
    const tipo = tipoSolicitacao(codigo);
    const cor = darkMode ? tipo.corEscura : tipo.cor;
    return (
        <MDBox display="flex" alignItems="center" gap={0.75}>
            <Icon sx={{ color: cor, fontSize: "1.1rem !important" }}>{tipo.icone}</Icon>
            <MDTypography variant="caption" fontWeight="medium" color="text" sx={{ whiteSpace: "nowrap" }}>{tipo.label}</MDTypography>
        </MDBox>
    );
}

function Idade({ ms, data, darkMode, rotulo }) {
    const cor = corIdade(ms, darkMode);
    return (
        <MDBox>
            <MDBox display="inline-flex" alignItems="center" gap={0.5} px={1} py={0.25}
                sx={{ borderRadius: "12px", border: `1px solid ${cor}`, background: comAlfa(cor, darkMode ? 0.18 : 0.08) }}>
                <Icon sx={{ color: cor, fontSize: "0.9rem !important" }}>schedule</Icon>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: `${cor} !important`, whiteSpace: "nowrap" }}>
                    {rotulo ? `${rotulo} ` : ""}{formatarDuracao(ms)}
                </MDTypography>
            </MDBox>
            {data && (
                <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8, mt: 0.25 }}>{data}</MDTypography>
            )}
        </MDBox>
    );
}

function Devedor({ s }) {
    return (
        <MDBox minWidth={0}>
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>{s.nomeDevedor || "-"}</MDTypography>
            <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.85 }}>
                {[s.cpfCnpjDevedor, s.nomeCliente].filter(Boolean).join(" · ")}
            </MDTypography>
        </MDBox>
    );
}

function Bloco({ rotulo, children, cor, darkMode }) {
    return (
        <MDBox>
            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>{rotulo}</MDTypography>
            <MDBox p={1.5} borderRadius="md"
                sx={{
                    background: cor ? comAlfa(cor, darkMode ? 0.16 : 0.07) : (darkMode ? "rgba(255,255,255,0.06)" : "#f0f2f5"),
                    borderLeft: cor ? `3px solid ${cor}` : "none",
                }}>
                <MDTypography variant="button" color="text" sx={{ whiteSpace: "pre-wrap", display: "block" }}>{children}</MDTypography>
            </MDBox>
        </MDBox>
    );
}

export default function FilaSolicitacoesConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userFunctionalities } = useUser();
    const { abrirArquivos } = useVisualizadorArquivos();
    const podeAtender = Array.isArray(userFunctionalities) && userFunctionalities.includes(PERMISSAO_FILA_SOLICITACOES);
    const corDe = (v) => (darkMode ? VISOES[v].corEscura : VISOES[v].cor);

    const [painel, setPainel] = useState(null);
    const [loading, setLoading] = useState(false);
    const [agora, setAgora] = useState(Date.now());
    const [visao, setVisao] = useState("fila");
    const [tipo, setTipo] = useState(OPCAO_TODOS);
    const [solicitante, setSolicitante] = useState(OPCAO_TODOS);
    const [busca, setBusca] = useState("");
    const [periodo, setPeriodo] = useState(PERIODOS_OPTIONS[1]);
    const [atualizacao, setAtualizacao] = useState(ATUALIZACAO_OPTIONS[2]);
    const [limite, setLimite] = useState(LIMITE_INICIAL);

    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");
    const sucesso = (msg) => { setSbMessage(msg); setSuccessSB(true); };
    const falha = (msg) => { setSbMessage(msg); setErrorSB(true); };

    const [loadingAction, setLoadingAction] = useState(false);

    // Detalhes (com linha do tempo)
    const [selecionada, setSelecionada] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [loadingHistorico, setLoadingHistorico] = useState(false);

    // Recusa / encerramento
    const [recusando, setRecusando] = useState(null);
    const [motivoRecusa, setMotivoRecusa] = useState("");
    const [encerrando, setEncerrando] = useState(null);
    const [obsEncerramento, setObsEncerramento] = useState("");

    // Ficha do devedor
    const [fichaDevedorId, setFichaDevedorId] = useState(null);

    // Aprovação de migração
    const [approvingSolicitacao, setApprovingSolicitacao] = useState(null);
    const [carteirasOptions, setCarteirasOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [statusFichaOptions, setStatusFichaOptions] = useState([]);
    const [approveCarteiraId, setApproveCarteiraId] = useState("");
    const [approveUsuarioId, setApproveUsuarioId] = useState("");
    const [approveStatusFichaId, setApproveStatusFichaId] = useState("");

    const ultimaRequisicao = useRef(0);
    const carregar = useCallback(() => {
        const requisicao = ++ultimaRequisicao.current;
        setLoading(true);
        get(`/solicitacoes-suporte/painel?dias=${periodo.value}`)
            .then(res => {
                if (requisicao !== ultimaRequisicao.current) return;
                setPainel(res || {});
                setAgora(Date.now());
            })
            .catch(err => falha(err.message || "Erro ao buscar as solicitações"))
            .finally(() => { if (requisicao === ultimaRequisicao.current) setLoading(false); });
    }, [periodo]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        if (!atualizacao.value) return undefined;
        const timer = setInterval(carregar, atualizacao.value * 1000);
        return () => clearInterval(timer);
    }, [atualizacao, carregar]);

    // Tempo decorrido anda sozinho entre as atualizações
    useEffect(() => {
        const timer = setInterval(() => setAgora(Date.now()), 60000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => { setLimite(LIMITE_INICIAL); }, [visao, tipo, solicitante, busca]);

    useEffect(() => {
        if (!approveCarteiraId) {
            setUsuariosOptions([]);
            return;
        }
        get(`/users/list-all-operators?carteiraId=${approveCarteiraId}`)
            .then(res => setUsuariosOptions(listaDe(res)))
            .catch(() => setUsuariosOptions([]));
    }, [approveCarteiraId]);

    const intervaloHoras = painel?.intervaloCobrancaHoras || 24;
    const filtrar = useCallback((lista) => (lista || []).filter(s =>
        (!tipo.value || s.tipoSolicitacao === tipo.value)
        && (!solicitante.value || s.nomeSolicitante === solicitante.value)
        && correspondeBusca(s, busca)), [tipo, solicitante, busca]);

    const listas = useMemo(() => ({
        fila: filtrar(painel?.fila),
        aguardando: filtrar(painel?.aguardandoSolicitante),
        concluidas: filtrar(painel?.concluidas),
    }), [painel, filtrar]);

    const solicitantesOptions = useMemo(() => {
        const nomes = new Set();
        [painel?.fila, painel?.aguardandoSolicitante, painel?.concluidas].forEach(l => (l || []).forEach(s => s.nomeSolicitante && nomes.add(s.nomeSolicitante)));
        return [OPCAO_TODOS, ...[...nomes].sort((a, b) => a.localeCompare(b, "pt-BR")).map(n => ({ id: n, value: n, label: n }))];
    }, [painel]);

    const indicadores = useMemo(() => {
        const maisAntiga = listas.fila.reduce((min, s) => (s.dataSolicitacaoMillis && s.dataSolicitacaoMillis < min ? s.dataSolicitacaoMillis : min), Infinity);
        const reenviadas = listas.fila.filter(s => s.reenviada).length;
        const prontas = listas.aguardando.filter(s => s.podeCobrar).length;
        const aprovadas = listas.concluidas.filter(s => s.status === "APROVADA");
        const tempos = aprovadas.filter(s => s.dataResolucaoMillis && s.dataSolicitacaoMillis).map(s => s.dataResolucaoMillis - s.dataSolicitacaoMillis);
        return {
            maisAntigaMs: Number.isFinite(maisAntiga) ? agora - maisAntiga : null,
            reenviadas,
            prontas,
            aprovadas: aprovadas.length,
            encerradas: listas.concluidas.length - aprovadas.length,
            tempoMedioMs: tempos.length ? tempos.reduce((a, b) => a + b, 0) / tempos.length : null,
        };
    }, [listas, agora]);

    const lista = listas[visao];
    const visiveis = lista.slice(0, limite);

    // ---------- Ações ----------
    const abrirDetalhes = (s) => {
        setSelecionada(s);
        setHistorico([]);
        setLoadingHistorico(true);
        get(`/solicitacoes-suporte/${s.id}/historico`)
            .then(res => setHistorico(listaDe(res)))
            .catch(() => setHistorico([]))
            .finally(() => setLoadingHistorico(false));
    };

    const depoisDaAcao = (msg) => {
        sucesso(msg);
        setSelecionada(null);
        carregar();
    };

    const openApproveMigracaoModal = (s) => {
        setApproveCarteiraId("");
        setApproveUsuarioId(""); // "" = mantém o responsável atual
        setApproveStatusFichaId(""); // "" = mantém o status atual
        setLoadingAction(true);
        Promise.all([
            get("/carteira/list-all").catch(() => null),
            get("/status-ficha/list-all").catch(() => null),
        ]).then(([cartRes, statusRes]) => {
            const carteiras = listaDe(cartRes).filter(c => !(c.nome || c.ccNome || "").toLowerCase().includes("processo"));
            setCarteirasOptions(carteiras);
            setStatusFichaOptions(listaDe(statusRes));
            // Pré-seleciona a carteira pedida pelo solicitante
            const pedida = carteiras.find(c => (c.nome || c.ccNome) === s.nomeCarteiraDestino);
            if (pedida) setApproveCarteiraId(pedida.id || pedida.cvId);
            setApprovingSolicitacao(s);
        }).finally(() => setLoadingAction(false));
    };

    const aprovar = async (s) => {
        if (s.tipoSolicitacao === "MIGRACAO_CARTEIRA") {
            openApproveMigracaoModal(s);
            return;
        }
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${s.id}/aprovar`, {});
            depoisDaAcao("Solicitação aprovada. A devolução foi efetivada e registrada na ficha.");
        } catch (err) {
            falha(err.message || "Erro ao aprovar a solicitação");
        } finally {
            setLoadingAction(false);
        }
    };

    const confirmarMigracao = async () => {
        if (!approveCarteiraId) {
            falha("Selecione a carteira de destino.");
            return;
        }
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${approvingSolicitacao.id}/aprovar`, {
                carteiraId: approveCarteiraId,
                usuarioId: approveUsuarioId || null,
                statusFichaId: approveStatusFichaId || null,
            });
            setApprovingSolicitacao(null);
            depoisDaAcao("Migração aprovada e efetivada.");
        } catch (err) {
            falha(err.message || "Erro ao aprovar a migração");
        } finally {
            setLoadingAction(false);
        }
    };

    const confirmarRecusa = async () => {
        if (!motivoRecusa.trim()) {
            falha("Informe o motivo da recusa.");
            return;
        }
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${recusando.id}/rejeitar`, { motivo: motivoRecusa.trim() });
            setRecusando(null);
            depoisDaAcao(`Solicitação recusada. ${recusando.nomeSolicitante || "O solicitante"} recebe o aviso no sino de notificações.`);
        } catch (err) {
            falha(err.message || "Erro ao recusar a solicitação");
        } finally {
            setLoadingAction(false);
        }
    };

    const cobrar = async (s) => {
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${s.id}/cobrar`, {});
            depoisDaAcao(`${s.nomeSolicitante || "Solicitante"} cobrado. O aviso aparece no sino de notificações.`);
        } catch (err) {
            falha(err.message || "Erro ao cobrar o solicitante");
        } finally {
            setLoadingAction(false);
        }
    };

    const cobrarProntas = async () => {
        setLoadingAction(true);
        try {
            const res = await post("/solicitacoes-suporte/cobrar-prontas", {});
            const qtd = res?.cobradas || 0;
            depoisDaAcao(qtd === 0 ? "Nenhuma solicitação liberada para cobrança agora." : `${qtd} ${qtd === 1 ? "solicitante cobrado" : "solicitantes cobrados"}.`);
        } catch (err) {
            falha(err.message || "Erro ao cobrar os solicitantes");
        } finally {
            setLoadingAction(false);
        }
    };

    const confirmarEncerramento = async () => {
        setLoadingAction(true);
        try {
            await postNoResponse(`/solicitacoes-suporte/${encerrando.id}/encerrar`, { observacao: obsEncerramento.trim() || null });
            setEncerrando(null);
            depoisDaAcao("Solicitação encerrada.");
        } catch (err) {
            falha(err.message || "Erro ao encerrar a solicitação");
        } finally {
            setLoadingAction(false);
        }
    };

    // Anexos da solicitação para a janela flutuante: os da ficha e o anexo de migração antigo (gravado fora da
    // ficha, antes dos anexos irem para a aba Documentos)
    const arquivosDaSolicitacao = (s) => [
        ...(s.anexos || []).map(a => ({ url: `/dossie/download-file/${a.id}`, nome: a.nome, tipo: a.tipo })),
        ...(s.nomeArquivo ? [{ url: `/solicitacoes-suporte/${s.id}/download-anexo`, nome: s.nomeArquivo, tipo: "Anexo", antigo: true }] : []),
    ];

    const baixarArquivo = (arq) => {
        getFile(arq.url, arq.nome || "anexo").catch(err => falha(err.message || "Erro ao baixar o anexo"));
    };

    const abrirRecusa = (s) => { setMotivoRecusa(""); setRecusando(s); };
    const abrirEncerramento = (s) => { setObsEncerramento(""); setEncerrando(s); };

    // ---------- Colunas por lista ----------
    const botao = (titulo, icone, cor, onClick, disabled) => (
        <Tooltip title={titulo}>
            <span>
                <IconButton size="small" onClick={onClick} disabled={disabled || loadingAction} aria-label={titulo}>
                    <Icon color={disabled ? "disabled" : cor} sx={{ fontSize: "1.35rem !important" }}>{icone}</Icon>
                </IconButton>
            </span>
        </Tooltip>
    );

    const proximaCobranca = (s) => {
        if (s.podeCobrar) return { texto: "Cobrança liberada", liberada: true };
        if (!s.proximaCobrancaMillis) return { texto: "-", liberada: false };
        return { texto: `Libera em ${formatarDuracao(s.proximaCobrancaMillis - agora)}`, liberada: false };
    };

    const COLUNAS = {
        fila: [
            { titulo: "Na fila há", largura: "13%", render: s => (
                <>
                    <Idade ms={agora - (s.dataSolicitacaoMillis || agora)} data={s.dataSolicitacao} darkMode={darkMode} />
                    {s.reenviada && (
                        <Chip size="small" color="info" variant="outlined" icon={<Icon>replay</Icon>} label="Reenviada" sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
                    )}
                </>
            ) },
            { titulo: "Tipo", largura: "15%", render: s => <TipoSolicitacao codigo={s.tipoSolicitacao} darkMode={darkMode} /> },
            { titulo: "Devedor", largura: "24%", render: s => <Devedor s={s} /> },
            { titulo: "O que pede", largura: "18%", render: s => {
                const qtdAnexos = (s.anexos?.length || 0) + (s.nomeArquivo ? 1 : 0);
                return (
                    <MDBox>
                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>{alvoSolicitacao(s)}</MDTypography>
                        {qtdAnexos > 0 && (
                            <MDBox display="flex" alignItems="center" gap={0.25} sx={{ opacity: 0.85 }}>
                                <Icon sx={{ fontSize: "0.9rem !important" }}>attach_file</Icon>
                                <MDTypography variant="caption" color="text">{qtdAnexos === 1 ? "1 anexo" : `${qtdAnexos} anexos`}</MDTypography>
                            </MDBox>
                        )}
                    </MDBox>
                );
            } },
            { titulo: "Solicitante", largura: "15%", render: s => <MDTypography variant="caption" color="text">{s.nomeSolicitante || "-"}</MDTypography> },
            { titulo: "Ações", largura: "15%", render: s => (
                <MDBox display="flex" flexWrap="nowrap">
                    {botao("Analisar", "search", "info", () => abrirDetalhes(s))}
                    {podeAtender && botao(s.tipoSolicitacao === "MIGRACAO_CARTEIRA" ? "Aprovar migração" : "Aprovar devolução", "check_circle", "success", () => aprovar(s))}
                    {podeAtender && botao("Recusar", "cancel", "error", () => abrirRecusa(s))}
                </MDBox>
            ) },
        ],
        aguardando: [
            { titulo: "Recusada há", largura: "13%", render: s => (
                <>
                    <Idade ms={agora - (s.dataResolucaoMillis || agora)} data={s.dataResolucao} darkMode={darkMode} />
                    {s.nomeResponsavel && (
                        <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8 }}>por {s.nomeResponsavel}</MDTypography>
                    )}
                </>
            ) },
            { titulo: "Tipo", largura: "14%", render: s => <TipoSolicitacao codigo={s.tipoSolicitacao} darkMode={darkMode} /> },
            { titulo: "Devedor", largura: "19%", render: s => <Devedor s={s} /> },
            { titulo: "Solicitante", largura: "12%", render: s => <MDTypography variant="caption" color="text">{s.nomeSolicitante || "-"}</MDTypography> },
            { titulo: "Motivo da recusa", largura: "18%", render: s => (
                <Tooltip title={s.motivoRecusa || ""}>
                    <MDTypography variant="caption" color="text"
                        sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {s.motivoRecusa || "-"}
                    </MDTypography>
                </Tooltip>
            ) },
            { titulo: "Cobranças", largura: "12%", render: s => {
                const proxima = proximaCobranca(s);
                return (
                    <MDBox>
                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>
                            {s.qtdCobrancas > 0 ? `${s.qtdCobrancas}× · última ${s.ultimaCobranca}` : "Nenhuma"}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                            <Icon sx={{ fontSize: "0.9rem !important", color: proxima.liberada ? corDe("concluidas") : "inherit", opacity: proxima.liberada ? 1 : 0.7 }}>
                                {proxima.liberada ? "notifications_active" : "snooze"}
                            </Icon>
                            <MDTypography variant="caption" color="text" sx={{ opacity: proxima.liberada ? 1 : 0.8 }}>{proxima.texto}</MDTypography>
                        </MDBox>
                    </MDBox>
                );
            } },
            { titulo: "Ações", largura: "12%", render: s => (
                <MDBox display="flex" flexWrap="nowrap">
                    {botao("Detalhes", "search", "info", () => abrirDetalhes(s))}
                    {podeAtender && botao(s.podeCobrar ? "Cobrar o solicitante" : `Cobrança liberada a cada ${intervaloHoras}h`, "notifications_active", "warning", () => cobrar(s), !s.podeCobrar)}
                    {podeAtender && botao("Encerrar sem a posição do solicitante", "lock", "secondary", () => abrirEncerramento(s))}
                </MDBox>
            ) },
        ],
        concluidas: [
            { titulo: "Concluída em", largura: "14%", render: s => <MDTypography variant="caption" color="text">{s.dataResolucao || "-"}</MDTypography> },
            { titulo: "Tipo", largura: "15%", render: s => <TipoSolicitacao codigo={s.tipoSolicitacao} darkMode={darkMode} /> },
            { titulo: "Devedor", largura: "22%", render: s => <Devedor s={s} /> },
            { titulo: "Solicitante", largura: "13%", render: s => <MDTypography variant="caption" color="text">{s.nomeSolicitante || "-"}</MDTypography> },
            { titulo: "Resultado", largura: "16%", render: s => (s.status === "APROVADA"
                ? <Chip size="small" color="success" icon={<Icon>check_circle</Icon>} label="Aprovada" sx={{ height: 22, fontSize: 11 }} />
                : <Chip size="small" variant="outlined" icon={<Icon>block</Icon>} label={s.dataPosicao ? "Recusada · solicitante ciente" : "Recusada · encerrada"} sx={{ height: 22, fontSize: 11 }} />) },
            { titulo: "Tempo total", largura: "10%", render: s => (
                <MDTypography variant="caption" color="text">
                    {s.dataResolucaoMillis && s.dataSolicitacaoMillis ? formatarDuracao(s.dataResolucaoMillis - s.dataSolicitacaoMillis) : "-"}
                </MDTypography>
            ) },
            { titulo: "", largura: "10%", render: s => botao("Detalhes", "search", "info", () => abrirDetalhes(s)) },
        ],
    };

    const MENSAGEM_VAZIA = {
        fila: "Fila vazia: nenhuma solicitação aguardando análise.",
        aguardando: "Nenhuma recusa aguardando a posição do solicitante.",
        concluidas: `Nenhuma solicitação concluída nos últimos ${periodo.value} dias.`,
    };

    const filtrando = !!(tipo.value || solicitante.value || busca.trim());

    return (
        <MDBox pb={3}>
            <MDSnackbar color="success" icon="check" title="Sucesso" content={sbMessage} open={successSB}
                onClose={() => setSuccessSB(false)} close={() => setSuccessSB(false)} bgWhite />
            <MDSnackbar color="error" icon="warning" title="Erro" content={sbMessage} open={errorSB}
                onClose={() => setErrorSB(false)} close={() => setErrorSB(false)} bgWhite />

            <Grid container spacing={3}>
                {/* Indicadores (clique troca a lista) */}
                <Grid item xs={12} sm={6} lg={3}>
                    <Indicador icone={VISOES.fila.icone} cor={corDe("fila")} rotulo={VISOES.fila.rotulo}
                        valor={painel ? listas.fila.length : "-"}
                        detalhe={!painel ? null : listas.fila.length === 0 ? "nada aguardando análise"
                            : `mais antiga há ${formatarDuracao(indicadores.maisAntigaMs)}${indicadores.reenviadas ? ` · ${indicadores.reenviadas} reenviada${indicadores.reenviadas > 1 ? "s" : ""}` : ""}`}
                        selecionado={visao === "fila"} onClick={() => setVisao("fila")} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <Indicador icone={VISOES.aguardando.icone} cor={corDe("aguardando")} rotulo={VISOES.aguardando.rotulo}
                        valor={painel ? listas.aguardando.length : "-"}
                        detalhe={painel ? `${indicadores.prontas} liberada${indicadores.prontas === 1 ? "" : "s"} para cobrança` : null}
                        selecionado={visao === "aguardando"} onClick={() => setVisao("aguardando")} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <Indicador icone={VISOES.concluidas.icone} cor={corDe("concluidas")} rotulo={`${VISOES.concluidas.rotulo} (${periodo.value} dias)`}
                        valor={painel ? listas.concluidas.length : "-"}
                        detalhe={painel ? `${indicadores.aprovadas} aprovada${indicadores.aprovadas === 1 ? "" : "s"} · ${indicadores.encerradas} recusada${indicadores.encerradas === 1 ? "" : "s"}` : null}
                        selecionado={visao === "concluidas"} onClick={() => setVisao("concluidas")} />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <Indicador icone="timer" cor={COR_TEMPO_MEDIO[darkMode ? "dark" : "light"]} rotulo="Tempo médio até aprovar"
                        valor={indicadores.tempoMedioMs !== null ? formatarDuracao(indicadores.tempoMedioMs) : "-"}
                        detalhe={`da solicitação à efetivação (${periodo.value} dias)`} />
                </Grid>

                {/* Filtros */}
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? ESCURO.cartao : "#fafafa", borderColor: darkMode ? ESCURO.divisoria : "#ddd" }}>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={6} md={3}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Buscar</MDTypography>
                                <MDInput fullWidth value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Devedor, CPF/CNPJ, cliente ou contrato" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Tipo</MDTypography>
                                <MDSelect name="tipo" value={tipo} options={TIPOS_OPTIONS} onChange={v => setTipo(v || OPCAO_TODOS)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Solicitante</MDTypography>
                                <MDSelect name="solicitante" value={solicitante} options={solicitantesOptions} onChange={v => setSolicitante(v || OPCAO_TODOS)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Concluídas</MDTypography>
                                <MDSelect name="periodo" value={periodo} options={PERIODOS_OPTIONS} onChange={v => setPeriodo(v || PERIODOS_OPTIONS[1])} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={1.5}>
                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={rotuloFiltro}>Atualizar a cada</MDTypography>
                                <MDSelect name="atualizacao" value={atualizacao} options={ATUALIZACAO_OPTIONS} onChange={v => setAtualizacao(v || ATUALIZACAO_OPTIONS[0])} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={1.5} sx={{ display: "flex", alignItems: "flex-end", pt: { sm: "38px !important" } }}>
                                <MDButton variant="gradient" color="info" onClick={carregar} disabled={loading} fullWidth sx={{ height: 40, px: 1 }}>
                                    <Icon sx={{ mr: 0.5 }}>refresh</Icon>Atualizar
                                </MDButton>
                            </Grid>
                            <Grid item xs={12} sx={{ pt: "8px !important" }}>
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                    Clique em um indicador para trocar a lista. A fila está em ordem de chegada (a mais antiga primeiro); o tempo fica
                                    verde até 1 dia, amarelo até 3 dias e vermelho depois disso.
                                </MDTypography>
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block", opacity: 0.85 }}>
                                    Ao recusar, o solicitante recebe o aviso no sino de notificações e responde: ciente (encerra) ou reenviar com nova
                                    justificativa (volta para a fila). O suporte pode cobrá-lo a cada {intervaloHoras}h
                                    (Parametrização do Sistema &gt; Regras de Processamento). A ficha recebe ocorrência ao solicitar e ao aprovar;
                                    recusa, cobrança e reenvio ficam só na linha do tempo da solicitação. Os anexos ficam na aba Documentos da ficha.
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                {/* Lista */}
                <Grid item xs={12}>
                    <Card variant="outlined" sx={{ background: darkMode ? ESCURO.cartao : "#fafafa", borderColor: darkMode ? ESCURO.divisoria : "#ddd", overflow: "hidden" }}>
                        <MDBox height={4}>{loading && <LinearProgress color="info" />}</MDBox>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} px={2} pt={1.5} pb={1}>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <Icon sx={{ color: corDe(visao) }}>{VISOES[visao].icone}</Icon>
                                <MDTypography variant="button" fontWeight="bold" color="text">
                                    {VISOES[visao].rotulo}: {lista.length} {lista.length === 1 ? "solicitação" : "solicitações"}
                                    {filtrando ? " (filtradas)" : ""}
                                </MDTypography>
                            </MDBox>
                            {visao === "aguardando" && podeAtender && (
                                <MDButton variant="outlined" color="warning" size="small" onClick={cobrarProntas}
                                    disabled={loadingAction || indicadores.prontas === 0}>
                                    <Icon sx={{ mr: 0.5 }}>notifications_active</Icon>
                                    Cobrar todas liberadas ({indicadores.prontas})
                                </MDButton>
                            )}
                        </MDBox>

                        {!painel && loading ? (
                            <MDBox display="flex" justifyContent="center" alignItems="center" p={5}><CircularProgress color="info" /></MDBox>
                        ) : lista.length === 0 ? (
                            <MDBox py={5} px={2} textAlign="center">
                                <Icon sx={{ fontSize: "2.2rem !important", color: corDe(visao), opacity: 0.7 }}>{filtrando ? "filter_alt_off" : "check_circle_outline"}</Icon>
                                <MDTypography variant="button" color="text" sx={{ display: "block", mt: 1 }}>
                                    {filtrando ? "Nenhuma solicitação com esses filtros." : MENSAGEM_VAZIA[visao]}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <TableContainer sx={{ boxShadow: "none", borderRadius: 0, background: "transparent" }}>
                                <Table sx={{ minWidth: 900 }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            {COLUNAS[visao].map((c, i) => (
                                                <DataTableHeadCell key={i} width={c.largura} sorted={false}>{c.titulo}</DataTableHeadCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visiveis.map(s => (
                                            <TableRow key={s.id} hover>
                                                {COLUNAS[visao].map((c, i) => (
                                                    <TableCell key={i} sx={{ ...celula, borderColor: darkMode ? "rgba(255,255,255,0.12)" : undefined }}>{c.render(s)}</TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {lista.length > limite && (
                            <MDBox display="flex" justifyContent="center" py={2}>
                                <MDButton variant="text" color="info" onClick={() => setLimite(l => l + LIMITE_INICIAL)}>
                                    Mostrar mais ({lista.length - limite} restantes)
                                </MDButton>
                            </MDBox>
                        )}
                    </Card>
                </Grid>
            </Grid>

            {/* Detalhes da solicitação */}
            <Dialog open={!!selecionada} onClose={() => setSelecionada(null)} maxWidth="md" fullWidth>
                {selecionada && (
                    <>
                        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <TipoSolicitacao codigo={selecionada.tipoSolicitacao} darkMode={darkMode} />
                            <Chip size="small" label={{
                                PENDENTE: selecionada.reenviada ? "Na fila (reenviada)" : "Na fila",
                                REJEITADA: "Recusada · aguardando solicitante",
                                APROVADA: "Aprovada",
                                ENCERRADA: "Recusada · encerrada",
                            }[selecionada.status] || selecionada.status} sx={{ height: 22, fontSize: 11 }} />
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={7}>
                                    <MDBox display="flex" flexDirection="column" gap={2}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sm={6}>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>Devedor</MDTypography>
                                                <Devedor s={selecionada} />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>O que pede</MDTypography>
                                                <MDTypography variant="caption" color="text">{alvoSolicitacao(selecionada)}</MDTypography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>Solicitante</MDTypography>
                                                <MDTypography variant="caption" color="text">{selecionada.nomeSolicitante || "-"} · {selecionada.dataSolicitacao}</MDTypography>
                                            </Grid>
                                            {selecionada.nomeResponsavel && (
                                                <Grid item xs={12} sm={6}>
                                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>
                                                        {selecionada.status === "APROVADA" ? "Aprovada por" : "Recusada por"}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" color="text">{selecionada.nomeResponsavel}</MDTypography>
                                                </Grid>
                                            )}
                                        </Grid>

                                        <Bloco rotulo="Justificativa da solicitação" darkMode={darkMode}>
                                            {selecionada.observacao || "Nenhuma observação informada."}
                                        </Bloco>
                                        {selecionada.motivoRecusa && (
                                            <Bloco rotulo={selecionada.status === "PENDENTE" ? "Motivo da recusa anterior" : "Motivo da recusa"} cor={darkMode ? "#EF5350" : "#C62828"} darkMode={darkMode}>
                                                {selecionada.motivoRecusa}
                                            </Bloco>
                                        )}
                                        {selecionada.posicaoSolicitante && (
                                            <Bloco rotulo={`Posição do solicitante${selecionada.dataPosicao ? ` (${selecionada.dataPosicao})` : ""}`} cor={darkMode ? "#64B5F6" : "#1565C0"} darkMode={darkMode}>
                                                {selecionada.posicaoSolicitante}
                                            </Bloco>
                                        )}

                                        <MDBox>
                                                <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>
                                                    Anexos da solicitação
                                                </MDTypography>
                                                {!(selecionada.anexos?.length > 0 || selecionada.nomeArquivo) && (
                                                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>Nenhum anexo enviado.</MDTypography>
                                                )}
                                                <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                                    {arquivosDaSolicitacao(selecionada).map((arq, indice) => (
                                                        <MDBox key={arq.url} display="inline-flex" alignItems="center">
                                                            <Chip size="small" variant="outlined" color="info" icon={<Icon>visibility</Icon>}
                                                                label={arq.nome} clickable
                                                                onClick={() => abrirArquivos(arquivosDaSolicitacao(selecionada), indice)}
                                                                title={`${arq.tipo || "Documento"}${arq.antigo ? "" : " (aba Documentos da ficha)"}. Clique para visualizar.`} />
                                                            <Tooltip title="Baixar">
                                                                <IconButton size="small" onClick={() => baixarArquivo(arq)} aria-label={`Baixar ${arq.nome}`}>
                                                                    <Icon color="info" sx={{ fontSize: "1.1rem !important" }}>download</Icon>
                                                                </IconButton>
                                                            </Tooltip>
                                                        </MDBox>
                                                    ))}
                                                </MDBox>
                                        </MDBox>

                                        <MDBox display="flex" gap={1} flexWrap="wrap">
                                            {selecionada.idDevedor && (
                                                <MDButton variant="outlined" color="info" size="small" onClick={() => setFichaDevedorId(selecionada.idDevedor)}>
                                                    <Icon sx={{ mr: 0.5 }}>person_search</Icon>Abrir ficha do devedor
                                                </MDButton>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} md={5}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 1 }}>Linha do tempo</MDTypography>
                                    {loadingHistorico ? (
                                        <CircularProgress size={20} color="info" />
                                    ) : historico.length === 0 ? (
                                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>
                                            Sem registros (solicitação anterior à linha do tempo).
                                        </MDTypography>
                                    ) : (
                                        <MDBox>
                                            {historico.map((h, i) => {
                                                const acao = acaoHistorico(h.acao);
                                                return (
                                                    <MDBox key={i} display="flex" gap={1.25} pb={i < historico.length - 1 ? 1.5 : 0} position="relative">
                                                        {i < historico.length - 1 && (
                                                            <MDBox position="absolute" left="11px" top="24px" bottom={0} width="2px"
                                                                sx={{ background: darkMode ? "rgba(255,255,255,0.15)" : "#e0e0e0" }} />
                                                        )}
                                                        <Icon color={acao.cor} sx={{ fontSize: "1.5rem !important", zIndex: 1 }}>{acao.icone}</Icon>
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block" }}>{acao.label}</MDTypography>
                                                            <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.8 }}>
                                                                {[h.data, h.usuario].filter(Boolean).join(" · ")}
                                                            </MDTypography>
                                                            {h.descricao && (
                                                                <MDTypography variant="caption" color="text" sx={{ display: "block", whiteSpace: "pre-wrap" }}>{h.descricao}</MDTypography>
                                                            )}
                                                        </MDBox>
                                                    </MDBox>
                                                );
                                            })}
                                        </MDBox>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <MDButton onClick={() => setSelecionada(null)} color="dark" variant="text">Fechar</MDButton>
                            {podeAtender && selecionada.status === "PENDENTE" && (
                                <>
                                    <MDButton onClick={() => abrirRecusa(selecionada)} color="error" variant="gradient" disabled={loadingAction}>Recusar</MDButton>
                                    <MDButton onClick={() => aprovar(selecionada)} color="success" variant="gradient" disabled={loadingAction}>
                                        {loadingAction ? <CircularProgress size={20} color="inherit" />
                                            : selecionada.tipoSolicitacao === "MIGRACAO_CARTEIRA" ? "Aprovar migração" : "Aprovar devolução"}
                                    </MDButton>
                                </>
                            )}
                            {podeAtender && selecionada.status === "REJEITADA" && (
                                <>
                                    <MDButton onClick={() => abrirEncerramento(selecionada)} color="secondary" variant="outlined" disabled={loadingAction}>Encerrar</MDButton>
                                    <Tooltip title={selecionada.podeCobrar ? "" : `Cobrança liberada a cada ${intervaloHoras}h`}>
                                        <span>
                                            <MDButton onClick={() => cobrar(selecionada)} color="warning" variant="gradient" disabled={loadingAction || !selecionada.podeCobrar}>
                                                Cobrar solicitante
                                            </MDButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Aprovação de migração */}
            <Dialog open={!!approvingSolicitacao} onClose={() => setApprovingSolicitacao(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Aprovar migração de carteira</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color="text" mb={2}>
                        Confirme a carteira de destino e, se precisar, defina um novo responsável e status para a ficha (ou mantenha os atuais).
                    </MDTypography>
                    <MDInput select fullWidth label="Carteira de destino" value={approveCarteiraId} onChange={(e) => setApproveCarteiraId(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}>
                        <MenuItem value="">Selecione...</MenuItem>
                        {carteirasOptions.map((c, i) => <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>)}
                    </MDInput>
                    <MDInput select fullWidth label="Novo responsável" value={approveUsuarioId} onChange={(e) => setApproveUsuarioId(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}>
                        <MenuItem value="">Manter o responsável atual</MenuItem>
                        {usuariosOptions.map((c, i) => <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>)}
                    </MDInput>
                    <MDInput select fullWidth label="Novo status da ficha" value={approveStatusFichaId} onChange={(e) => setApproveStatusFichaId(e.target.value)}
                        InputLabelProps={{ shrink: true }}>
                        <MenuItem value="">Manter o status atual</MenuItem>
                        {statusFichaOptions.map((c, i) => <MenuItem key={i} value={c.id || c.cvId}>{c.nome || c.ccNome}</MenuItem>)}
                    </MDInput>
                </DialogContent>
                <DialogActions>
                    <MDButton color="secondary" variant="text" onClick={() => setApprovingSolicitacao(null)}>Cancelar</MDButton>
                    <MDButton color="success" variant="gradient" onClick={confirmarMigracao} disabled={loadingAction}>
                        {loadingAction ? <CircularProgress size={20} color="inherit" /> : "Confirmar e aprovar"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Recusa */}
            <Dialog open={!!recusando} onClose={() => setRecusando(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Recusar solicitação</DialogTitle>
                <DialogContent>
                    {recusando && (
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                            {tipoSolicitacao(recusando.tipoSolicitacao).label} · {recusando.nomeDevedor} · solicitada por {recusando.nomeSolicitante || "-"}
                        </MDTypography>
                    )}
                    <TextField fullWidth multiline rows={4} variant="outlined" autoFocus value={motivoRecusa}
                        onChange={(e) => setMotivoRecusa(e.target.value)} placeholder="Motivo da recusa" label="Motivo da recusa (obrigatório)" />
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.85 }}>
                        O solicitante vê este motivo no sino de notificações e responde: ciente (encerra) ou reenviar com nova justificativa
                        (volta para a fila). Nada é gravado na ficha.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setRecusando(null)} color="dark" variant="text">Cancelar</MDButton>
                    <MDButton onClick={confirmarRecusa} color="error" variant="gradient" disabled={loadingAction || !motivoRecusa.trim()}>
                        {loadingAction ? <CircularProgress size={20} color="inherit" /> : "Confirmar recusa"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Encerramento sem posição do solicitante */}
            <Dialog open={!!encerrando} onClose={() => setEncerrando(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Encerrar solicitação recusada</DialogTitle>
                <DialogContent>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                        Encerra a recusa sem esperar a posição de {encerrando?.nomeSolicitante || "o solicitante"} (por exemplo, usuário desligado).
                        O aviso some do sino dele.
                    </MDTypography>
                    <TextField fullWidth multiline rows={3} variant="outlined" value={obsEncerramento}
                        onChange={(e) => setObsEncerramento(e.target.value)} label="Observação (opcional)" />
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setEncerrando(null)} color="dark" variant="text">Cancelar</MDButton>
                    <MDButton onClick={confirmarEncerramento} color="secondary" variant="gradient" disabled={loadingAction}>
                        {loadingAction ? <CircularProgress size={20} color="inherit" /> : "Encerrar"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Ficha do devedor */}
            <Dialog open={!!fichaDevedorId} onClose={() => setFichaDevedorId(null)} fullWidth maxWidth="xl"
                PaperProps={{ style: { backgroundColor: darkMode ? ESCURO.cartao : "#fafafa", minHeight: "80vh" } }}>
                <DialogContent sx={{ p: 1 }}>
                    {fichaDevedorId && (
                        <BaseDeDadosCreateEdit recordId={fichaDevedorId} closeCreateEdit={() => setFichaDevedorId(null)} isViewOnly={true} />
                    )}
                </DialogContent>
            </Dialog>
        </MDBox>
    );
}
